import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Coins, ChevronDown } from 'lucide-react';

const CreditsBadge = ({ className = '' }) => {
  const { user } = useAuth();
  const [displayCredits, setDisplayCredits] = useState(user?.credits_balance || 0);
  const [isAnimating, setIsAnimating] = useState(false);
  const [prevCredits, setPrevCredits] = useState(user?.credits_balance || 0);

  useEffect(() => {
    const newCredits = user?.credits_balance || 0;
    
    // Detect if credits changed
    if (newCredits !== prevCredits && prevCredits !== 0) {
      setIsAnimating(true);
      
      // Animate the counter
      const diff = newCredits - prevCredits;
      const steps = 20;
      const increment = diff / steps;
      let current = prevCredits;
      let step = 0;
      
      const timer = setInterval(() => {
        step++;
        current += increment;
        if (step >= steps) {
          setDisplayCredits(newCredits);
          clearInterval(timer);
          setTimeout(() => setIsAnimating(false), 500);
        } else {
          setDisplayCredits(Math.round(current));
        }
      }, 50);

      return () => clearInterval(timer);
    } else {
      setDisplayCredits(newCredits);
    }
    
    setPrevCredits(newCredits);
  }, [user?.credits_balance, prevCredits]);

  if (!user) return null;

  const formatCredits = (credits) => {
    if (credits >= 1000000) {
      return (credits / 1000000).toFixed(1) + 'M';
    } else if (credits >= 1000) {
      return (credits / 1000).toFixed(1) + 'K';
    }
    return credits.toString();
  };

  return (
    <Link 
      to="/credits"
      className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-amber-50 hover:bg-amber-100 border border-amber-200 transition-all ${
        isAnimating ? 'scale-110 ring-2 ring-amber-400 ring-opacity-50' : ''
      } ${className}`}
      data-testid="credits-badge"
    >
      <Coins className={`w-4 h-4 text-amber-500 ${isAnimating ? 'animate-bounce' : ''}`} />
      <span className={`text-sm font-semibold text-amber-700 ${isAnimating ? 'text-amber-600' : ''}`}>
        {formatCredits(displayCredits)}
      </span>
    </Link>
  );
};

export default CreditsBadge;
